import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatTime(s) {
  if (!s) return "0m";
  if (s < 60) return `${s}s`;
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [thought, setThought] = useState(null);

  useEffect(() => {
    api.get("/progress").then(({ data }) => setProgress(data)).catch(() => {});
    api.get("/daily-thought").then(({ data }) => setThought(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Your Study</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-2" data-testid="dashboard-title">
          {user?.name ? `Welcome back, ${user.name.split(" ")[0]}.` : "Welcome back."}
        </h1>
        <p className="text-sm text-forge-100/60 mb-6">Choose a path. Spend time with ideas worth your attention.</p>

        <div className="mb-12">
          {progress?.institution ? (
            <div className="inline-flex flex-wrap items-center gap-3 border border-ember-500/25 bg-ember-500/8 px-4 py-2 rounded-sm">
              <span className="text-[10px] uppercase tracking-[0.3em] text-ember-500">Institution Access</span>
              <span className="font-serif text-lg text-forge-100">{progress.institution.name}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-forge-100/45">{progress.institution.institution_role || "patron"}</span>
            </div>
          ) : (
            <Link to="/join-institution" className="inline-flex items-center gap-3 border border-white/10 hover:border-ember-500/50 px-4 py-2 rounded-sm transition-colors">
              <span className="text-[10px] uppercase tracking-[0.3em] text-forge-100/45">Have a library code?</span>
              <span className="text-sm text-ember-400">Join Institution</span>
            </Link>
          )}
        </div>

        {/* MODE CARDS */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 mb-16">
          {[
            { to: "/upload", label: "Upload Reading", desc: "PDF, TXT, or DOCX from your own collection.", tag: "01" },
            { to: "/library", label: "Public Library", desc: "Curated short works from the public domain.", tag: "02" },
            { to: "/generate", label: "AI Generated", desc: "Original essays on topics that sharpen thought.", tag: "03" },
            { to: "/writing", label: "Writing Mentor", desc: "Paste a draft for Socratic revision feedback.", tag: "04" },
          ].map((m) => (
            <Link
              key={m.to}
              to={m.to}
              className="bg-forge-900 hover:bg-forge-800 p-8 transition-all duration-300 hover:-translate-y-0.5 group"
              data-testid={`mode-${m.tag}`}
            >
              <div className="font-mono text-xs text-ember-500 mb-4">{m.tag}</div>
              <div className="font-serif text-2xl mb-3 group-hover:text-ember-400 transition-colors">{m.label}</div>
              <p className="text-sm text-forge-100/60 leading-relaxed">{m.desc}</p>
              <div className="mt-6 text-xs uppercase tracking-[0.25em] text-forge-100/40 group-hover:text-ember-500 transition-colors">Open →</div>
            </Link>
          ))}
        </div>

        {/* STATS + DAILY THOUGHT */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            <Stat label="Articles completed" value={progress?.articles_completed ?? 0} testid="stat-articles" />
            <Stat label="Reading time" value={formatTime(progress?.total_reading_seconds)} testid="stat-time" />
            <Stat label="Vocabulary learned" value={progress?.vocabulary_words_learned ?? 0} testid="stat-vocab" />
            <Stat label="Reflections" value={progress?.reflections_completed ?? 0} testid="stat-reflections" />
          </div>
          <div className="border border-white/10 p-8 rounded-sm">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">Daily Thought</div>
            {thought ? (
              <blockquote className="border-l-2 border-ember-500 pl-4">
                <p className="font-serif italic text-lg leading-snug">"{thought.text}"</p>
                <footer className="mt-3 text-xs uppercase tracking-[0.25em] text-forge-100/50">— {thought.author}</footer>
              </blockquote>
            ) : (
              <p className="text-sm text-forge-100/40">Loading…</p>
            )}
          </div>
        </div>

        {progress?.recent_sessions?.length > 0 && (
          <div className="mt-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Recent</div>
            <ul className="divide-y divide-white/5 border-t border-b border-white/5">
              {progress.recent_sessions.map((s) => (
                <li key={s.session_id} className="flex justify-between items-center py-4">
                  <span className="font-serif text-lg text-forge-100">{s.title}</span>
                  <span className="text-xs uppercase tracking-[0.25em] text-forge-100/40">{formatTime(s.duration_seconds)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-white/10 p-8 rounded-sm hover:border-ember-500/40 transition-colors" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-forge-100/50 mb-3">{label}</div>
      <div className="font-serif text-5xl text-forge-100">{value}</div>
    </div>
  );
}
